// How SB's formats draw on a slide, everywhere a slide shows (src/boards/slides/Boxes.tsx calls these): a gradient or a
// picture filling a shape or a text box, an outline's dashes and ends, the gallery's shadows, glows and soft edges, and a
// picture's crop, crop to a shape and adjustments. A box with none of them is drawn exactly as before, by Boxes.tsx.
//
// SVG ids are this drawing's own (useId), since one box is drawn in several places at once (the strip, the editor, the
// presenter's window): a gradient referred to across drawings stops showing when the first one is hidden.

import { useId, type CSSProperties, type ReactNode } from "react";
import { picturePath } from "../../../../shared/imageInfo";
import { colorValue, type Palette, type ShapeKind, type SlideBox, type SlideColor } from "../../../../shared/slideDesign";
import { FORMAT_KEYS, SHADE, adjustLinear, gradientAt, type BoxGradient, type DashStyle } from "../../../../shared/slideFormat";
import { readableOn } from "../../../themes/themes";
import { r2 } from "../../../../shared/slidePlace";

/** Whether a box wears any of SB's formats (else Boxes.tsx draws it as it always has). */
export function hasFormat(b: SlideBox): boolean {
  if (FORMAT_KEYS.some((k) => b[k] !== undefined)) return true;
  return !!b.dash && b.dash !== "dash" && b.dash !== "dot" && b.dash !== "solid";
}

/** A theme colour or a hex at an alpha, as CSS. */
export function rgba(c: SlideColor, palette: Palette, alpha = 1): string {
  const v = colorValue(c, palette, "#000000");
  if (alpha >= 1) return v;
  const m = /^#?([0-9a-f]{6})/i.exec(v);
  const n = m ? parseInt(m[1], 16) : 0;
  return `rgb(${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255} / ${r2(alpha)})`;
}

const hexOf = (palette: Palette) => (c: SlideColor) => colorValue(c, palette, "#000000");

// ---------------------------------------------------------------------------
// outlines

/** An outline's dashes as SVG's, for its width and ends (round or square ends reach past each dash, so the dash is cut). */
export function dashArray(dash: DashStyle | undefined, w: number, cap?: "round" | "square"): string | undefined {
  const k = Math.max(0.5, w);
  const ends = cap ? k : 0;
  const run = (xs: number[]) => xs.map((x, i) => r2(i % 2 === 0 ? Math.max(0.01, x * k - ends) : x * k + ends)).join(" ");
  switch (dash) {
    case "dash":
      return run([3, 2]);
    case "dot":
      return `0.01 ${r2(k * 2)}`;
    case "square":
      // PowerPoint's Square Dot (sysDash: three on, one off)
      return run([3, 1]);
    case "dashDot":
      return run([4, 3, 1, 3]);
    case "long":
      return run([8, 3]);
    case "longDashDot":
      return run([8, 3, 1, 3]);
    default:
      return undefined;
  }
}

/** An outline's ends as SVG's: round dots are round whatever the box says. */
export const lineCap = (b: Pick<SlideBox, "cap" | "dash">): "round" | "square" | "butt" => (b.dash === "dot" || b.cap === "round" ? "round" : b.cap === "square" ? "square" : "butt");

// ---------------------------------------------------------------------------
// effects

/** The box's shadow (from the gallery) and glow as a CSS filter, which follows the box's own outline; undefined for none. */
export function fxFilter(b: SlideBox, palette: Palette): string | undefined {
  const parts: string[] = [];
  if (b.glow) {
    const c = rgba(b.glow.color, palette, b.glow.alpha ?? 0.5);
    parts.push(`drop-shadow(0 0 ${r2(b.glow.size * 0.35)}px ${c})`, `drop-shadow(0 0 ${r2(b.glow.size * 0.65)}px ${c})`);
  }
  if (b.shade && !SHADE[b.shade].inner) {
    const s = SHADE[b.shade];
    parts.push(`drop-shadow(${s.x}px ${s.y}px ${s.blur / 2}px rgb(0 0 0 / ${s.alpha}))`);
  }
  return parts.length ? parts.join(" ") : undefined;
}

/** An inside shadow as CSS's inset box shadow (text boxes and pictures). */
export function innerShadow(b: SlideBox): string | undefined {
  if (!b.shade || !SHADE[b.shade].inner) return undefined;
  const s = SHADE[b.shade];
  return `inset ${s.x}px ${s.y}px ${s.blur}px rgb(0 0 0 / ${s.alpha})`;
}

/** Soft edges as a CSS mask: the box fades out over its last pixels (an ellipse fades round). */
export function softMask(soft: number | undefined, ellipse = false): CSSProperties {
  if (!soft) return {};
  if (ellipse) {
    const m = `radial-gradient(closest-side, #000 calc(100% - ${soft * 1.4}px), transparent)`;
    return { maskImage: m, WebkitMaskImage: m };
  }
  const x = `linear-gradient(to right, transparent, #000 ${soft}px, #000 calc(100% - ${soft}px), transparent)`;
  const y = `linear-gradient(to bottom, transparent, #000 ${soft}px, #000 calc(100% - ${soft}px), transparent)`;
  return { maskImage: `${x}, ${y}`, WebkitMaskImage: `${x}, ${y}`, maskComposite: "intersect", WebkitMaskComposite: "source-in" } as CSSProperties;
}

// ---------------------------------------------------------------------------
// fills

/** The ends of a gradient's line at an angle across a box, as CSS draws one (its corners on the first and last colour). */
function gradientLine(angle: number, w: number, h: number) {
  const a = (angle * Math.PI) / 180;
  const dx = Math.sin(a);
  const dy = -Math.cos(a);
  const half = (Math.abs(w * dx) + Math.abs(h * dy)) / 2;
  return { x1: w / 2 - dx * half, y1: h / 2 - dy * half, x2: w / 2 + dx * half, y2: h / 2 + dy * half };
}

/** A gradient as CSS. */
export function cssGradient(g: BoxGradient, palette: Palette): string {
  const stops = g.stops.map((s) => `${rgba(s.color, palette, s.alpha ?? 1)} ${r2(s.at * 100)}%`).join(", ");
  return g.radial ? `radial-gradient(ellipse farthest-corner at 50% 50%, ${stops})` : `linear-gradient(${g.angle}deg, ${stops})`;
}

/** A text box's background when it is a gradient or a picture. */
export function boxBackground(b: SlideBox, palette: Palette): CSSProperties | undefined {
  if (b.fillPic) {
    const url = `url("${picturePath(b.fillPic.image.key)}")`;
    if (b.fillPic.tile) {
      const tw = Math.max(16, (b.fillPic.image.w || 240) * (b.fillPic.scale ?? 1));
      return { backgroundImage: url, backgroundRepeat: "repeat", backgroundSize: `${r2(tw)}px auto`, backgroundPosition: "0 0" };
    }
    return { backgroundImage: url, backgroundSize: "cover", backgroundPosition: "50% 50%", backgroundRepeat: "no-repeat" };
  }
  if (b.grad) return { backgroundImage: cssGradient(b.grad, palette) };
  return undefined;
}

/** The colour words read in on a box's gradient (its middle), or null when its fill is not one. */
export function inkOnFill(b: SlideBox, palette: Palette): string | null {
  if (b.fillPic) return null;
  if (!b.grad) return null;
  const mid = gradientAt(b.grad, 0.5, hexOf(palette));
  if (mid.alpha < 0.5) return null;
  return readableOn(mid.hex) === "#ffffff" ? "#FFFFFF" : "#111317";
}

/** A shape's body: its outline path filled with its colour, gradient or picture, its outline drawn, with its effects. */
export function ShapeBody({ b, d, fill, stroke, lw, palette }: { b: SlideBox; d: string; fill: string; stroke: string; lw: number; palette: Palette }) {
  const uid = useId().replace(/[^A-Za-z0-9_-]/g, "");
  const id = (k: string) => `sb${uid}${k}`;
  const defs: ReactNode[] = [];
  let paint = fill;
  if (b.fillPic) {
    const img = b.fillPic.image;
    const tw = b.fillPic.tile ? Math.max(16, (img.w || 240) * (b.fillPic.scale ?? 1)) : b.w;
    const th = b.fillPic.tile ? tw * ((img.h || 240) / (img.w || 240)) : b.h;
    defs.push(
      <pattern key="p" id={id("p")} patternUnits="userSpaceOnUse" x={0} y={0} width={r2(tw)} height={r2(th)}>
        <image href={picturePath(img.key)} x={0} y={0} width={r2(tw)} height={r2(th)} preserveAspectRatio={b.fillPic.tile ? "none" : "xMidYMid slice"} />
      </pattern>,
    );
    paint = `url(#${id("p")})`;
  } else if (b.grad) {
    const g = b.grad;
    const stops = g.stops.map((s, i) => <stop key={i} offset={s.at} stopColor={colorValue(s.color, palette, "#000000")} stopOpacity={s.alpha ?? 1} />);
    if (g.radial) defs.push(<radialGradient key="g" id={id("g")} cx="0.5" cy="0.5" r="0.7071" gradientUnits="objectBoundingBox">{stops}</radialGradient>);
    else {
      const l = gradientLine(g.angle, b.w, b.h);
      defs.push(
        <linearGradient key="g" id={id("g")} gradientUnits="userSpaceOnUse" x1={r2(l.x1)} y1={r2(l.y1)} x2={r2(l.x2)} y2={r2(l.y2)}>
          {stops}
        </linearGradient>,
      );
    }
    paint = `url(#${id("g")})`;
  }
  let filter: string | undefined;
  const inner = b.shade && SHADE[b.shade].inner ? SHADE[b.shade] : null;
  if (inner || b.soft) {
    const s = b.soft ?? 0;
    defs.push(
      <filter key="f" id={id("f")} x="-20%" y="-20%" width="140%" height="140%" colorInterpolationFilters="sRGB">
        {s ? (
          <>
            <feMorphology in="SourceGraphic" operator="erode" radius={r2(s / 2)} result="er" />
            <feGaussianBlur in="er" stdDeviation={r2(s / 2)} result="base" />
          </>
        ) : (
          <feOffset in="SourceGraphic" dx={0} dy={0} result="base" />
        )}
        {inner && (
          <>
            <feComponentTransfer in="SourceAlpha" result="inv">
              <feFuncA type="table" tableValues="1 0" />
            </feComponentTransfer>
            <feGaussianBlur in="inv" stdDeviation={inner.blur / 2} result="blur" />
            <feOffset in="blur" dx={inner.x} dy={inner.y} result="off" />
            <feFlood floodColor="#000000" floodOpacity={inner.alpha} result="ink" />
            <feComposite in="ink" in2="off" operator="in" result="shade" />
            <feComposite in="shade" in2="SourceAlpha" operator="in" result="inside" />
            <feMerge>
              <feMergeNode in="base" />
              <feMergeNode in="inside" />
            </feMerge>
          </>
        )}
      </filter>,
    );
    filter = `url(#${id("f")})`;
  }
  return (
    <svg className="sl-bx-svg" width={b.w} height={b.h} viewBox={`0 0 ${b.w} ${b.h}`} aria-hidden>
      {defs.length > 0 && <defs>{defs}</defs>}
      <path d={d} fill={paint} filter={filter} />
      {lw > 0 && stroke !== "none" && <path d={d} fill="none" stroke={stroke} strokeWidth={lw} strokeDasharray={dashArray(b.dash, lw, b.cap)} strokeLinecap={lineCap(b)} strokeLinejoin={b.cap === "square" ? "miter" : "round"} />}
    </svg>
  );
}

/** A text box's outline drawn as a path (for dashes and ends CSS's borders do not have), centred on its edge as PowerPoint's. */
export function OutlineSvg({ b, d, color, lw }: { b: SlideBox; d: string; color: string; lw: number }) {
  return (
    <svg className="sl-bx-svg sl-bx-outline" width={b.w} height={b.h} viewBox={`0 0 ${b.w} ${b.h}`} aria-hidden>
      <path d={d} fill="none" stroke={color} strokeWidth={lw} strokeDasharray={dashArray(b.dash, lw, b.cap)} strokeLinecap={lineCap(b)} strokeLinejoin={b.cap === "square" ? "miter" : "round"} />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// pictures

/** The picture's place in its box: cropped (its whole frame placed so the part kept fills the box), or filling or fitting. */
function pictureStyle(b: SlideBox): CSSProperties {
  if (b.crop) {
    const fw = b.w / Math.max(0.02, 1 - b.crop.l - b.crop.r);
    const fh = b.h / Math.max(0.02, 1 - b.crop.t - b.crop.b);
    return { position: "absolute", left: r2(-b.crop.l * fw), top: r2(-b.crop.t * fh), width: r2(fw), height: r2(fh), maxWidth: "none", objectFit: "fill" };
  }
  const fx = (b.fx ?? 0.5) * 100;
  const fy = (b.fy ?? 0.5) * 100;
  return { objectFit: b.imgFit === "fit" ? "contain" : "cover", objectPosition: `${fx}% ${fy}%`, transform: b.zoom && b.zoom > 1 ? `scale(${b.zoom})` : undefined, transformOrigin: `${fx}% ${fy}%` };
}

/** Brightness and contrast as an SVG filter's id (this drawing's own), and saturation as CSS's. */
function useAdjust(b: SlideBox): { svg: ReactNode; filter: string | undefined } {
  const uid = useId().replace(/[^A-Za-z0-9_-]/g, "");
  const parts: string[] = [];
  let svg: ReactNode = null;
  if (b.bright || b.contrast) {
    const { slope, intercept } = adjustLinear(b.bright, b.contrast);
    const id = `sbadj${uid}`;
    svg = (
      <svg className="sl-bx-defs" width="0" height="0" aria-hidden focusable="false">
        <filter id={id} colorInterpolationFilters="sRGB">
          <feComponentTransfer>
            <feFuncR type="linear" slope={slope} intercept={intercept} />
            <feFuncG type="linear" slope={slope} intercept={intercept} />
            <feFuncB type="linear" slope={slope} intercept={intercept} />
          </feComponentTransfer>
        </filter>
      </svg>
    );
    parts.push(`url(#${id})`);
  }
  if (b.sat !== undefined && b.sat !== 1) parts.push(`saturate(${b.sat})`);
  return { svg, filter: parts.length ? parts.join(" ") : undefined };
}

/**
 * A picture box with SB's formats: cropped, cropped to a shape, adjusted, with its outline on the shape it is cut to and
 * its effects (the outer ones on the box, so they follow the shape; the inside shadow and soft edges in it).
 */
export function FormatPicture({ b, palette, shape, lazy, outer }: { b: SlideBox; palette: Palette; shape: (k: ShapeKind, w: number, h: number, r?: number) => string; lazy: boolean; outer: Record<string, unknown> & { style: CSSProperties } }) {
  const adj = useAdjust(b);
  const mask = b.mask;
  const d = mask ? shape(mask, b.w, b.h, mask === "round" ? (b.radius ?? Math.min(b.w, b.h) * 0.16) : b.radius) : b.radius ? shape("round", b.w, b.h, b.radius) : null;
  const lw = b.line ? (b.lineW ?? 4) : 0;
  const clip: CSSProperties = {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    ...(d ? { clipPath: `path("${d}")` } : {}),
    ...softMask(b.soft, mask === "ellipse"),
  };
  const inset = innerShadow(b);
  return (
    <div {...outer} style={{ ...outer.style, filter: fxFilter(b, palette) ?? outer.style.filter }}>
      {adj.svg}
      <div className="sl-bx-pic" style={clip}>
        <img className="sl-bx-img" src={picturePath(b.image!.key)} alt="" draggable={false} loading={lazy ? "lazy" : "eager"} decoding="async" style={{ ...pictureStyle(b), filter: adj.filter }} />
        {inset && <div className="sl-bx-inset" style={{ boxShadow: inset }} />}
      </div>
      {lw > 0 && b.line && <OutlineSvg b={b} d={d ?? `M0,0H${b.w}V${b.h}H0Z`} color={colorValue(b.line, palette, palette.text)} lw={lw} />}
    </div>
  );
}
